// ─── SSO(448) begin — remove with the SSO feature ───
import myaxios from "@/lib/axios";
// ─── SSO(448) end ───
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { buildReport, getPollingReport } from "@/queries/report";
import { switchSession } from "@/queries/user";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import Cookies from "js-cookie";
import { LogOut, MapPin, RefreshCw } from "lucide-react";
import { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Query keys that depend on report data and must be invalidated when a new report is ready.
 *
 * IMPORTANT: Add new query keys here when creating queries that:
 * - Accept a report ID parameter
 * - Display forecast/prediction data tied to a specific report
 * - Show zone, flight, or aggregate statistics from the current report
 *
 * These queries are automatically invalidated when:
 * - A new forecast is generated and becomes ready
 * - User loads existing new report data via the refresh button
 */
const REPORT_DEPENDENT_QUERY_KEYS = [
  "latest_report",
  "all_reports",
  "reports",
  "zone_data",
  "aggregate_data",
  "flights",
  "metric_breakdown",
] as const;

/**
 * Dropdown menu for switching between accessible airport sessions.
 * Only renders when user has access to 2+ sessions.
 * Switches session by updating auth token and forcing full page reload.
 */
export function SessionSwitcher() {
  const data = useSessionData();
  const { mutateAsync: getNewSession } = switchSession();

  /**
   * Switch to a different session by updating the auth token.
   * Forces full page reload to refresh all cached data.
   */
  const requestNewSession = async (id: number) => {
    const { success, token } = await getNewSession(id);
    if (success) {
      document.cookie = `auth_token=${token}; path=/`;

      // Redirect to "/" and force a full page reload
      window.location.href = "/";
    }
  };

  // Don't render if user only has access to one session
  if (data.access.length < 2) {
    return null;
  }

  return (
    <Menu as="div" className="relative ml-3">
      <div>
        <Menu.Button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 text-sm transition-all duration-200">
          <MapPin size={14} className="text-gray-300" />
          <span>{data.session_name}</span>
          <ChevronDownIcon className="w-4 h-4 opacity-70" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg py-1 focus:outline-none">
          {data.access.map((item) => (
            <Menu.Item key={item.id}>
              {({ active }) => (
                <a
                  onClick={() => requestNewSession(item.id)}
                  className={`${
                    active ? "bg-gray-700" : ""
                  } block px-4 py-2 text-sm text-gray-300 cursor-pointer hover:text-white flex items-center gap-2`}
                >
                  <MapPin size={14} className="text-gray-400" />
                  {item.name}
                </a>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

/**
 * Displays the last updated time for the current report.
 * Shows "Never" if no report has been generated yet.
 */
export function ReportDetails() {
  const { report } = useReportContext();
  const { airport_config } = useSessionData();

  return (
    <div className="text-sm flex items-center mr-3 hidden lg:flex">
      <span className="text-gray-400 mr-1.5 opacity-70">Last updated:</span>
      <span className="font-medium text-gray-300">
        {report?.ready_at
          ? new Date(report.ready_at).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
              timeZone: airport_config.timezone,
            })
          : "Never"}
      </span>
    </div>
  );
}

export function ReportRefresh() {
  // Track whether a report build is in progress
  const [refreshing, setRefreshing] = useState(false);

  // ID of the report we're polling for completion
  const [pollingReportId, setPollingReportId] = useState<number | null>(null);

  const { mutateAsync: runBuildReport } = buildReport();
  const queryClient = useQueryClient();

  const { toast } = useToast();

  /**
   * Invalidate all report-dependent queries to force refetch with fresh data.
   * Called when a new report is ready or when loading existing new data.
   *
   * Query keys are defined in REPORT_DEPENDENT_QUERY_KEYS constant at the top of this file.
   */
  const invalidateReportQueries = useCallback(() => {
    REPORT_DEPENDENT_QUERY_KEYS.forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  }, [queryClient]);

  // Poll for report status when we have a pollingReportId
  const { data: pollingReport } = getPollingReport(
    pollingReportId,
    !!pollingReportId
  );

  /**
   * Monitor the polling report status and handle state transitions.
   * When report is ready: invalidate queries and show notification.
   * When error/timeout: stop polling and show error notification.
   */
  useEffect(() => {
    if (pollingReport && pollingReportId) {
      // Check if the report we're polling for matches the latest report
      const isOurReport = pollingReport.id === pollingReportId;

      if (!isOurReport) {
        // Latest report ID doesn't match - still building or different report
        return;
      }

      switch (pollingReport.status) {
        case "ready":
          setPollingReportId(null);
          setRefreshing(false);

          // Invalidate all report-dependent queries to refetch with new report data
          invalidateReportQueries();

          // Show success notification
          toast({
            title: "Forecast Ready",
            description: "Your new forecast has been loaded successfully.",
            duration: 5000,
          });
          break;
        case "error":
          setPollingReportId(null);
          setRefreshing(false);
          toast({
            title: "Refresh Failed",
            description:
              "There was an error building the forecast. Please try again.",
            variant: "destructive",
            duration: 10000,
          });
          break;
        case "timeout":
          setPollingReportId(null);
          setRefreshing(false);
          toast({
            title: "Refresh Timeout",
            description:
              "The forecast generation took too long. Please try again later.",
            variant: "destructive",
            duration: 10000,
          });
          break;
        case "building":
          // Still building - continue polling
          break;
        default:
          console.warn("Unknown report status:", pollingReport.status);
          break;
      }
    }
  }, [pollingReport, pollingReportId, toast, invalidateReportQueries]);

  /**
   * Cleanup effect: clear polling state on unmount.
   * Prevents memory leaks and unwanted state after component unmounts.
   */
  useEffect(() => {
    return () => {
      setPollingReportId(null);
    };
  }, []);

  /**
   * Handle refresh button click - triggers a new forecast build.
   * User-initiated refresh starts a 2-3 minute background build process.
   * When complete, report queries are invalidated so the latest data reloads.
   */
  const onRefreshClick = async () => {
    setRefreshing(true);

    try {
      const newReport = await runBuildReport();

      if (!newReport || !newReport.id) {
        toast({
          title: "Refresh Failed",
          description: "Failed to create a new forecast. Please try again.",
          variant: "destructive",
        });
        setRefreshing(false);
        return;
      }

      setPollingReportId(newReport.id);

      toast({
        title: "Refreshing Forecast",
        description:
          "Please wait 2-3 minutes to prepare a new forecast. We'll notify you when it's ready.",
      });
    } catch (error) {
      console.error("Build report error:", error);
      toast({
        title: "Refresh Failed",
        description: "There was an error refreshing the forecast.",
        variant: "destructive",
      });
      setRefreshing(false);
      setPollingReportId(null);
    }
  };

  return (
    <div className="items-center hidden lg:flex gap-1">
      <ReportDetails />
      <div className="relative">
        <Button
          variant="outline"
          type="button"
          onClick={onRefreshClick}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200"
        >
          <RefreshCw
            size={16}
            className={`${refreshing ? "animate-spin" : ""}`}
          />
          <span>Refresh Forecast</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * User profile dropdown menu with logout functionality.
 * Displays user initial in avatar badge and truncated name/email.
 * Logout clears all cookies and redirects to login page.
 */
export function ProfileMenu() {
  const navigate = useNavigate();
  const { name, email } = useSessionData();
  // ─── SSO(448) begin — remove with the SSO feature ───
  const { is_sso } = useSessionData();
  // ─── SSO(448) end ───

  /**
   * Clear all cookies and redirect to login page.
   */
  const handleLogout = async () => {
    // ─── SSO(448) begin — remove with the SSO feature ───
    // An SSO session is also ended at the identity provider, so the next sign-in
    // prompts for credentials rather than silently reusing the IdP session. The
    // API deletes the session row and returns the provider's logout URL; a
    // password session gets no URL and falls through to the local path below.
    if (is_sso) {
      try {
        const token = Cookies.get("auth_token");
        const { data } = await myaxios.post("/api/auth/sso/logout", { token });
        const cookies = Cookies.get();
        for (const cookie in cookies) {
          Cookies.remove(cookie);
        }
        if (data?.logout_url) {
          window.location.href = data.logout_url;
          return;
        }
        navigate("/login");
        return;
      } catch {
        // Fall through to the local logout below.
      }
    }
    // ─── SSO(448) end ───

    const cookies = Cookies.get();
    for (const cookie in cookies) {
      Cookies.remove(cookie);
    }

    navigate("/login");
  };

  /**
   * Get first letter of name or email for avatar display.
   * Fallback to "U" if neither is available.
   */
  const getInitial = () => {
    if (name && name.length > 0) return name[0].toUpperCase();
    if (email && email.length > 0) return email[0].toUpperCase();
    return "U";
  };

  return (
    <Menu as="div" className="relative ml-3">
      <div>
        <Menu.Button className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 text-sm transition-all duration-200">
          <div className="w-5 h-5 shrink-0 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
            {getInitial()}
          </div>
          <span className="max-w-[100px] truncate hidden sm:inline">
            {name.length > 2 ? name : email}
          </span>
          <ChevronDownIcon className="w-4 h-4 shrink-0 opacity-70" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg py-1 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <a
                onClick={handleLogout}
                className={`${
                  active ? "bg-gray-700" : ""
                } block px-4 py-2 text-sm text-gray-300 cursor-pointer hover:text-white flex items-center gap-2`}
              >
                <LogOut size={14} className="text-gray-400" />
                Sign out
              </a>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
